//  @ts-ignore
import React, { useEffect, useRef, useState } from 'react';
// import '@sbmdkl/nepali-datepicker-reactjs/dist/index.css';
import { Button, DatePicker, Popover, Radio } from 'antd';
import styled from 'styled-components';
import { Prettify } from 'common/helper/typeHelper';
import {
  SectionResults,
  StudentDetails,
  Subject,
  TestTypeAndContributionPair,
} from 'data/result/result.model';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import useTermResult from '../hooks/useTermResult';
import GroupingTable, { Column } from './GroupingTable';
import { InfoWrapper, TestInfo } from './result.style';
import PrintResults from './PrintResults';
import PrintBulkMarkSheets from './components/PrintBulkMarkSheets';
import { Section } from 'data/grade/grade.model';
import useSchoolConfig from 'features/schoolConfig/hooks/useSchoolConfig';
import { useSubject } from '../hooks/useSubject';
import PrintBulk from './components/TestMarksheet/PrintBulk';
import Calendar from 'common/component/KCalendar';
import '@sbmdkl/nepali-datepicker-reactjs/dist/index.css';

interface ViewTerminalResultsProps {
  examYearId: string;
  terminalExamId: string;
  classId: string;
  sections?: Section[];
}

const PrintWrapper = styled.section`
  section {
    width: 100%;
    overflow-x: auto;
  }

  .printHeader {
    .school__info {
      width: 100%;
      text-align: center;
    }

    display: none;
  }

  @media print {
    padding: 1em;

    .printHeader {
      display: block;
    }

    .test__info {
      font-size: 12px;
    }

    table {
      height: 100%;

      thead {
        td {
          font-weight: 400;
        }
      }
    }
  }
`;

const nf = Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
});

const ViewTerminalResults = ({
  examYearId,
  classId,
  terminalExamId,
  sections,
}: ViewTerminalResultsProps): JSX.Element => {
  const { fetchAggregatedResultForView } = useTermResult();
  const [showPrint, setShowPrint] = React.useState<boolean>(false);
  const [showPrintMarkSheet, setShowPrintMarkSheet] = React.useState<boolean>(false);
  const [showPrintMarkSheetAggregate, setShowPrintMarkSheetAggregate] = React.useState<boolean>(
    false,
  );
  const { fetchSubject } = useSubject();
  const { configData, fetchSchoolConfig, isLoading } = useSchoolConfig();
  const [issueDate, setIssueDate] = React.useState<string | null>();
  const [data, setData] = useState<SectionResults[]>([]);
  const [subjects, setSubjcts] = useState<Subject[]>([]);
  const [section, setSections] = useState();
  const [sectionsAndResults, setSectionsAndResults] = useState<
    Array<{
      name: string;
      results: SectionResults[];
    }>
  >([]);
  const [test_types_list, settest_types_list] = useState<Array<string>>([]);
  const [testAndContribution, settestAndContribution] = useState<
    Array<{
      id: string;
      name: string;
      contribution: string;
    }>
  >([]);

  const togglePrint = (): void => {
    setShowPrint(!showPrint);
  };

  const togglePrintMarkSheet = (): void => {
    setShowPrintMarkSheet(!showPrintMarkSheet);
  };

  const togglePrintMarkSheetAggregate = (): void => {
    setShowPrintMarkSheetAggregate(!showPrintMarkSheetAggregate);
  };

  const termViewResults = useSelector((state: RootState) => state.termAggregatedResults);

  const examIdName = React.useMemo(() => {
    const tp = 'terminal'.length;
    const xm = examYearId.length;

    return terminalExamId.slice(tp + xm + 2);
  }, [terminalExamId, examYearId]);

  const printResultTypes = React.useMemo(() => {
    return (
      <>
        <Button type="link" onClick={togglePrint}>
          Print Ledger
        </Button>
        <Button type="link" onClick={togglePrintMarkSheet}>
          Print Marksheets
        </Button>
        <Button type="link" onClick={togglePrintMarkSheetAggregate}>
          Print Aggregates
        </Button>
      </>
    );
  }, []);

  const tableColumns = React.useMemo(() => {
    return [
      {
        id: 'studentRoll',
        label: 'Roll',
        maxWidth: 80,
      },
      {
        id: 'studentName',
        label: 'Student',
        align: 'left',
      },
      ...subjects.map((sub) => ({
        id: sub.subjectId,
        label: sub.subjectTitle,
        children: [
          ...testAndContribution.map((tt, index) => {
            return {
              id: tt.id,
              label: tt.name,
            };
          }),
          {
            id: 'total',
            label: 'Total',
          },
        ],
      })),
      {
        id: 'sum-total',
        label: 'Total',
      },
      {
        id: 'percent-total',
        label: '%',
        maxWidth: 80,
      },
      {
        id: 'position',
        label: 'Pos',
        maxWidth: 80,
      },
    ] as Column[];
  }, [subjects]);

  React.useEffect(() => {
    fetchAggregatedResultForView(examYearId, terminalExamId, classId);
  }, [examYearId, classId, terminalExamId]);

  React.useEffect(() => {
    if (termViewResults.status === 'data' && !isLoading) {
      const sectionsAndResults = termViewResults.data.sections;
      const tempSubjects = termViewResults.data.subjects;
      const test_types_list = termViewResults?.data?.testTypeAndContributionPair.map((test) => {
        return Object.keys(test)[0];
      });
      const testAndContribution = termViewResults.data?.testTypeAndContributionPair.map(
        (test, index) => {
          const testId = Object.keys(test)[0];
          return {
            id: Object.keys(test)[0],
            name: Object.keys(test)[0].slice(0, 4),
            contribution: test[testId as keyof typeof test]?.toString(),
          };
        },
      );
      setSectionsAndResults(sectionsAndResults);
      settest_types_list(test_types_list);
      settestAndContribution(testAndContribution);
      setSubjcts(tempSubjects);
      const tempResults = sectionsAndResults.map((sec) => [...sec.results]).flat(1);
      setData(tempResults);
    }
  }, [termViewResults]);
  React.useEffect(() => {
    if (configData) return;
    fetchSchoolConfig();
    fetchSubject();
  }, []);

  return (
    <>
      <InfoWrapper>
        <div className="meta">
          <div className="info__item">
            <div className="info__title">Year</div>
            <div className="info__value">{examYearId}</div>
          </div>
          <div className="info__item">
            <div className="info__title">Term</div>
          </div>
          <div className="info__item">
            <div className="info__title">Class</div>
            <div className="info__value">Class {classId}</div>
          </div>
        </div>
        <div className="controls">
          <Calendar
            style={{ width: 250 }}
            language="en"
            placeholder="Select Date of Issue"
            onChange={({ bsDate }: any) => {
              setIssueDate(bsDate || null);
            }}
          />
          <Popover
            content={issueDate ? printResultTypes : 'Select an issue date first!!!'}
            trigger={issueDate ? ['click'] : ['hover']}
            placement="left"
          >
            <Button disabled={!issueDate}>Print</Button>
          </Popover>
        </div>
      </InfoWrapper>

      <TestInfo>
        <div>
          {testAndContribution.map((test) => {
            return (
              <div>
                {test.name} {test.contribution}
              </div>
            );
          })}
        </div>
      </TestInfo>

      {sectionsAndResults.map((section) => {
        return (
          <>
            <p style={{ margin: '10px 0', fontWeight: 'bold' }}>
              Class: {classId} Section: {section.name}
            </p>
            <section>
              <GroupingTable
                data={section.results}
                columns={tableColumns}
                tests={testAndContribution.map((test) => test.name)}
                loading={termViewResults.status === 'loading'}
              />
            </section>
          </>
        );
      })}

      {showPrint && issueDate ? (
        <PrintResults
          metaSchool={{
            principal: '',
            principalSignature: 'string',
            examCoordinator: 'string',
            examCoordinatorSignature: '',
            schoolName: configData?.data.schoolDetails.schoolName,
            schoolLogo: configData?.data.schoolDetails.schoolLogo,
            schoolAddress: configData?.data.schoolDetails.schoolAddress,
          }}
          issueDate={issueDate}
          data={sectionsAndResults.map((section) => section.results)}
          columns={tableColumns}
          test_types={test_types_list}
          isLoading={termViewResults.status === 'loading'}
          meta={{
            classId,
            examYearId,
            examId: examIdName,
            sections: sections,
          }}
          onFinish={(): void => {
            setShowPrint(false);
          }}
        />
      ) : null}

      {showPrintMarkSheet && issueDate ? (
        <PrintBulk
          data={{
            metaSchool: {
              principal: configData?.data.schoolDetails.principal,
              principalSignature: configData?.data.schoolDetails.principalSignature,
              examCoordinator: 'string',
              examCoordinatorSignature: '',
              schoolName: configData?.data.schoolDetails.schoolName,
              schoolLogo: configData?.data.schoolDetails.schoolLogo,
              schoolAddress: configData?.data.schoolDetails.schoolAddress,
            },
            issueDate: issueDate,
            examYearId: examYearId,
            testExamId: terminalExamId,
            sectionId: '',
            records: data.map((d) => {
              return {
                studentName: d.name,
                totalFM: d.results.totalFM.toString(),
                totalOM: d.results.totalOM.toString(),
                overAllGpa: d.results.overAllGpa.toString(),
                position: d.results.position.toString(),
                percentage: d.results.percentage.toString(),
                remarks: d.results.remarks,
                studentRoll: Number(d.results.position),
                subjects: d.results.subjects.map((sub) => {
                  return {
                    subjectId: sub.subjectId,
                    title: sub.subject,
                    full_mark: sub.totalFM.toString(),
                    pass_mark: '40',
                    obtained_mark: sub.totalOM.toString(),
                    gpa: sub.overAllGpa,
                  };
                }),
              };
            }),
          }}
          onFinish={(): void => {
            setShowPrintMarkSheet(false);
          }}
        />
      ) : null}

      {showPrintMarkSheetAggregate && issueDate ? (
        <PrintBulkMarkSheets
          metaSchool={{
            principal: configData?.data.schoolDetails.principal,
            principalSignature: configData?.data.schoolDetails.principalSignature,
            examCoordinator: '',
            examCoordinatorSignature: '',
            schoolName: configData?.data.schoolDetails.schoolName,
            schoolLogo: configData?.data.schoolDetails.schoolLogo,
            schoolAddress: configData?.data.schoolDetails.schoolAddress,
          }}
          issueDate={issueDate}
          examYearId={examYearId}
          terminalExamId={terminalExamId}
          records={data}
          testsandContribution={testAndContribution}
          onFinish={(): void => {
            setShowPrintMarkSheetAggregate(false);
          }}
        />
      ) : null}
    </>
  );
};
export default ViewTerminalResults;
